import React, { FC, useCallback, useState } from 'react'
import clsx from 'clsx'
import {
  useContentRefreshDomains,
  useGetProjectDomain,
  useMessage,
} from '@pepper/ui'

import SEOTable from 'container/SEOTools/components/SEOTable/SEOTable'

import { useKeywordLabelling } from 'utils/hooks/IdeaLab/useKeywordLabelling'
import { useSelectedCompanyId } from 'utils/functions/misc'

import { getColumns } from './columns'

import { Keyword } from 'types/keyword'

import s from './Table.module.css'

type Props = {
  isDomainRemoved: boolean
  loading: boolean
  totalRecords: number
  projectId: string
  page: number
  total: number
  rowsSelected: React.Key[]
  onSelect: (keys: React.Key[]) => void
  onPageChange: (page: number) => void
  onPageSizeChange: (limit: number) => void
  onCreateTopics: (themeKeywordId: string) => void
  onDeleteKeyword: (keyword: Keyword) => void
  limit?: number
  dataSource?: Keyword[]
}

export const Table: FC<Props> = ({
  dataSource,
  loading,
  totalRecords,
  onSelect,
  onPageChange,
  rowsSelected,
  onCreateTopics,
  onDeleteKeyword,
  projectId,
  page,
  total,
  limit,
  onPageSizeChange,
  isDomainRemoved,
}) => {
  const companyId = useSelectedCompanyId()
  const [keywordId, setKeywordId] = useState('')
  const { showError } = useMessage()

  const { isGoogleConnected } = useContentRefreshDomains(companyId)

  const {
    data: keywordLabelling,
    isLoading: isLoadingKeywordLabel,
    error: labellingError,
  } = useKeywordLabelling({
    id: keywordId,
    enabled: keywordId.length > 0 && !!companyId,
    companyId,
  })

  const getRowKey = useCallback(
    (row: Keyword) => {
      return row.id
    },
    [dataSource]
  )

  const { data: projectDomain } = useGetProjectDomain({
    data: {
      companyId,
      projectId,
    },
  })

  if (labellingError) {
    showError(labellingError)
  }

  return (
    <div className={s.table}>
      <SEOTable
        rowKey={getRowKey}
        className={clsx({
          [s.hasSelectedRows]: rowsSelected.length,
        })}
        loading={loading}
        columns={getColumns({
          totalRecords,
          onCreateTopics,
          onDeleteKeyword,
          isDomainRemoved,
          isLoadingKeywordLabel,
          onGetKeywordLabel: setKeywordId,
          pageRankingResults: keywordLabelling,
          domain: projectDomain?.domain.domain_url,
          companyId,
          isGoogleConnected,
        })}
        dataSource={dataSource}
        rowSelection={{
          type: 'checkbox',
          onChange: onSelect,
          selectedRowKeys: rowsSelected,
        }}
        rowClassName={s.row}
        pagination={{
          hideOnSinglePage: true,
          onChange: onPageChange,
          total,
          current: page,
          pageSizeOptions: ['10', '25', '50', '100'],
          onShowSizeChange: (_, size) => onPageSizeChange(size),
          showSizeChanger: true,
          pageSize: limit,
          showTotal: (_total, range) =>
            `Showing ${range[0]} to ${range[1]} of ${_total} keywords`,
        }}
      />
    </div>
  )
}
